/**
 * Channel Publication Import Renderer
 *
 * Displays import batch process details:
 * operation description and source channel with icon.
 *
 * Data shape (from ChannelPublicationImportBatchProcessStartedEventSchema):
 *   connectorId: string
 *   integrationUserId: string
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconPlug } from '@tabler/icons-react';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, resolved, t }) => {
  const data = ( process.data || {}) as ProcessData;
  const connectorId = data.connectorId;
  const resolvedChannel = connectorId ? resolved.resolvedChannels[connectorId] : undefined;

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_ChannelPublicationImport_description' )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_sourceChannel' )}:
        </Text>
        <ThemeIcon size="sm" variant="light" color="gray">
          <IconPlug size={14} />
        </ThemeIcon>
        <Text fz="sm">
          {resolvedChannel?.name ?? connectorId ?? t( 'batchProcesses_renderer_none' )}
        </Text>
      </Group>
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, _process, resolvedChannels ) => {
    const connectorId = data?.connectorId;
    if ( connectorId ) {
      const resolved = resolvedChannels?.[connectorId];
      const channelName = resolved?.name ?? connectorId;
      return t( 'batchProcesses_summary_fromChannel', { channel: channelName });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => {
    const data = ( process?.data || {}) as ProcessData;
    return {
      channelIds: data.connectorId ? [data.connectorId] : []
    };
  }
};

export default DetailComponent;
